import { useEffect, useState } from 'react';
import api from '../../services/ApiService';

type Artwork = {
  refs: number;
  promise: Promise<string | null>;
  url: string | null;
  abort: AbortController;
};
const artwork = new Map<string, Artwork>();
/** Share protected artwork only among mounted cards in the same auth generation. */
export function acquirePassportArtwork(id: string, generation: number) {
  const key = `${generation}:${id}`;
  let entry = artwork.get(key);
  if (!entry) {
    const abort = new AbortController();
    const created: Artwork = { refs: 0, url: null, abort, promise: Promise.resolve(null) };
    created.promise = fetch(`${api.baseUrl}/passport_card_art.php?id=${encodeURIComponent(id)}`, {
      headers: api.getAuthHeaders(),
      credentials: 'include',
      cache: 'no-store',
      signal: abort.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Artwork unavailable');
        return response.blob();
      })
      .then((blob) => {
        if (abort.signal.aborted || generation !== api.sessionGeneration) return null;
        created.url = URL.createObjectURL(blob);
        return created.url;
      })
      .catch(() => null);
    artwork.set(key, created);
    entry = created;
  }
  entry.refs++;
  const shared = entry;
  let released = false;
  return {
    promise: shared.promise,
    release: () => {
      if (released) return;
      released = true;
      if (--shared.refs === 0) {
        shared.abort.abort();
        if (shared.url) URL.revokeObjectURL(shared.url);
        artwork.delete(key);
      }
    },
  };
}
export function usePassportArtwork(id?: string) {
  const generation = api.sessionGeneration;
  const key = `${generation}:${id || ''}`;
  const [value, setValue] = useState<{ key: string; url: string | null }>();
  useEffect(() => {
    if (!id) return;
    let active = true;
    const resource = acquirePassportArtwork(id, generation);
    void resource.promise.then((url) => {
      if (active && generation === api.sessionGeneration) setValue({ key, url });
    });
    return () => {
      active = false;
      resource.release();
    };
  }, [id, generation, key]);
  return value?.key === key ? value.url : null;
}
